import React, { useState, useEffect } from "react";
import { View, StyleSheet, TextInput } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { EmergencyContactType } from "@/types/db/user_emergency_contacts";
import { formatPhoneNumber } from "@/utils/phoneValidation";
import { Button } from "@/components/ui/Button";
import Badge from "@/components/ui/Badge";
import { Menu } from "@/components/Menu";

type PhoneType = "cell" | "home" | "work";
type EmailType = "personal" | "work";

type PhoneData = {
  type: PhoneType;
  number: string;
};

type EmailData = {
  type: EmailType;
  address: string;
};

const RELATIONSHIP_OPTIONS = [
  { text: "Spouse", bgColor: "#10b981" },
  { text: "Parent", bgColor: "#3b82f6" },
  { text: "Sibling", bgColor: "#8b5cf6" },
  { text: "Child", bgColor: "#f59e0b" },
  { text: "Friend", bgColor: "#ec4899" },
  { text: "Other", bgColor: "#6b7280" },
];

const PHONE_TYPE_OPTIONS = [
  { text: "cell", bgColor: "#10b981" },
  { text: "home", bgColor: "#3b82f6" },
  { text: "work", bgColor: "#8b5cf6" },
];

const EMAIL_TYPE_OPTIONS = [
  { text: "personal", bgColor: "#10b981" },
  { text: "work", bgColor: "#3b82f6" },
];

export interface EmergencyContactCardProps {
  contact: EmergencyContactType;
  index: number;
  canUpdate: boolean;
  canDelete: boolean;
  onUpdate: (index: number, updates: Partial<EmergencyContactType>) => void;
  onSave: (index: number) => void;
  onRemove: (index: number) => void;
}

export function EmergencyContactCard({
  contact,
  index,
  canUpdate,
  canDelete,
  onUpdate,
  onSave,
  onRemove,
}: EmergencyContactCardProps) {
  const { colors } = useTheme();
  const isNewContact = !contact.id;

  // Local edit state - new contacts start in edit mode
  const [isEditing, setIsEditing] = useState(isNewContact);

  // When a new contact is added, enter edit mode
  useEffect(() => {
    if (isNewContact) {
      setIsEditing(true);
    }
  }, [isNewContact]);

  const phones = Array.isArray(contact.phones) ? (contact.phones as PhoneData[]) : [];
  const emails = Array.isArray(contact.emails) ? (contact.emails as EmailData[]) : [];

  // Phone handlers
  const handleAddPhone = () => {
    const usedTypes = new Set(phones.map((p) => p.type));
    const availableTypes: PhoneType[] = ["cell", "home", "work"];
    const nextType = availableTypes.find((type) => !usedTypes.has(type));
    if (!nextType) return;

    const newPhone: PhoneData = { type: nextType, number: "" };
    onUpdate(index, { phones: [...phones, newPhone] });
  };

  const handleRemovePhone = (phoneIndex: number) => {
    const updated = phones.filter((_, i) => i !== phoneIndex);
    onUpdate(index, { phones: updated });
  };

  const handleUpdatePhone = (phoneIndex: number, updates: Partial<PhoneData>) => {
    const updated = [...phones];
    if (updates.number !== undefined) {
      updates.number = formatPhoneNumber(updates.number);
    }
    updated[phoneIndex] = { ...updated[phoneIndex], ...updates };
    onUpdate(index, { phones: updated });
  };

  // Email handlers
  const handleAddEmail = () => {
    const usedTypes = new Set(emails.map((e) => e.type));
    const availableTypes: EmailType[] = ["personal", "work"];
    const nextType = availableTypes.find((type) => !usedTypes.has(type));
    if (!nextType) return;

    const newEmail: EmailData = { type: nextType, address: "" };
    onUpdate(index, { emails: [...emails, newEmail] });
  };

  const handleRemoveEmail = (emailIndex: number) => {
    const updated = emails.filter((_, i) => i !== emailIndex);
    onUpdate(index, { emails: updated });
  };

  const handleUpdateEmail = (emailIndex: number, updates: Partial<EmailData>) => {
    const updated = [...emails];
    updated[emailIndex] = { ...updated[emailIndex], ...updates };
    onUpdate(index, { emails: updated });
  };

  const fullName = `${contact.first_name || ""} ${contact.last_name || ""}`.trim();

  const handleSaveAndClose = async () => {
    await onSave(index);
    setIsEditing(false);
  };

  const handleCancel = () => {
    // If it's a new contact with no data, remove it
    if (isNewContact && !contact.first_name && !contact.last_name) {
      onRemove(index);
    }
    setIsEditing(false);
  };

  // View mode - show when not editing
  if (!isEditing) {
    return (
      <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
        <View style={styles.itemContent}>
          <View style={styles.itemInfo}>
            <View style={styles.itemHeader}>
              <ThemedText type="defaultSemiBold">{fullName || "Unnamed Contact"}</ThemedText>
              {contact.relationship && <Badge label="Relationship" tone="purple" text={contact.relationship} />}
            </View>

            {(phones.length > 0 || emails.length > 0) && (
              <View style={styles.contactDetails}>
                {phones.map((phone, pIndex) => (
                  <ThemedText key={`phone-${pIndex}`} style={[styles.detailText, { color: colors.textSecondary }]}>
                    <ThemedText style={{ textTransform: "capitalize", fontWeight: "500" }}>{phone.type}:</ThemedText>{" "}
                    {phone.number}
                  </ThemedText>
                ))}
                {emails.map((email, eIndex) => (
                  <ThemedText key={`email-${eIndex}`} style={[styles.detailText, { color: colors.textSecondary }]}>
                    <ThemedText style={{ textTransform: "capitalize", fontWeight: "500" }}>{email.type}:</ThemedText>{" "}
                    {email.address}
                  </ThemedText>
                ))}
              </View>
            )}
          </View>

          {/* Menu for edit/delete actions based on permissions */}
          {(canUpdate || canDelete) && (
            <Menu
              buttonStyle={{
                alignSelf: "center",
                borderWidth: 0,
                backgroundColor: "transparent",
              }}
              hideArchive={true}
              hideDuplicate={true}
              onEdit={canUpdate ? () => setIsEditing(true) : undefined}
              onDelete={canDelete ? () => onRemove(index) : undefined}
            />
          )}
        </View>
      </View>
    );
  }

  // Edit mode
  return (
    <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
      <View style={styles.editForm}>
        {/* Name row */}
        <View style={styles.row}>
          <TextInput
            style={[
              styles.input,
              styles.flexInput,
              { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
            ]}
            placeholder="First name *"
            placeholderTextColor={colors.placeholder}
            value={contact.first_name || ""}
            onChangeText={(value) => onUpdate(index, { first_name: value })}
          />
          <TextInput
            style={[
              styles.input,
              styles.flexInput,
              { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
            ]}
            placeholder="Last name *"
            placeholderTextColor={colors.placeholder}
            value={contact.last_name || ""}
            onChangeText={(value) => onUpdate(index, { last_name: value })}
          />
          <Button icon="close" style={[styles.cancelBtn, { borderColor: colors.border }]} onPress={handleCancel} />
          <Button
            icon="content-save"
            style={[styles.saveBtn, { backgroundColor: colors.primary }]}
            textStyle={{ color: "white" }}
            onPress={handleSaveAndClose}
          />
        </View>

        {/* Relationship */}
        <View style={styles.row}>
          <ThemedText style={[styles.label, { color: colors.textSecondary }]}>Relationship:</ThemedText>
          <TextInput
            style={[
              styles.input,
              styles.flexInput,
              { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
            ]}
            placeholder="e.g. Spouse, Parent, Sibling"
            placeholderTextColor={colors.placeholder}
            value={contact.relationship || ""}
            onChangeText={(value) => onUpdate(index, { relationship: value })}
          />
        </View>

        {/* Phones section */}
        <View style={styles.subSection}>
          <View style={styles.subSectionHeader}>
            <ThemedText style={[styles.label, { color: colors.textSecondary }]}>Phones</ThemedText>
            <Button
              icon="plus"
              text="Add"
              style={[styles.smallBtn, { borderColor: colors.border }]}
              textStyle={{ fontSize: 12 }}
              onPress={handleAddPhone}
              disabled={phones.length >= 3}
            />
          </View>
          {phones.length > 0 && (
            <View style={styles.subItems}>
              {phones.map((phone, phoneIndex) => (
                <View key={phoneIndex} style={styles.row}>
                  <TextInput
                    style={[
                      styles.input,
                      styles.typeInput,
                      { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
                    ]}
                    placeholder="Type"
                    placeholderTextColor={colors.placeholder}
                    value={phone.type}
                    onChangeText={(value) => handleUpdatePhone(phoneIndex, { type: value as PhoneType })}
                  />
                  <TextInput
                    style={[
                      styles.input,
                      styles.flexInput,
                      { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
                    ]}
                    placeholder="(555) 555-5555"
                    placeholderTextColor={colors.placeholder}
                    value={phone.number}
                    onChangeText={(value) => handleUpdatePhone(phoneIndex, { number: value })}
                    keyboardType="phone-pad"
                  />
                  <Button
                    icon="close"
                    style={[styles.removeBtn, { backgroundColor: colors.dangerBackground }]}
                    textStyle={{ color: colors.dangerText }}
                    onPress={() => handleRemovePhone(phoneIndex)}
                  />
                </View>
              ))}
            </View>
          )}
        </View>

        {/* Emails section */}
        <View style={styles.subSection}>
          <View style={styles.subSectionHeader}>
            <ThemedText style={[styles.label, { color: colors.textSecondary }]}>Emails</ThemedText>
            <Button
              icon="plus"
              text="Add"
              style={[styles.smallBtn, { borderColor: colors.border }]}
              textStyle={{ fontSize: 12 }}
              onPress={handleAddEmail}
              disabled={emails.length >= 2}
            />
          </View>
          {emails.length > 0 && (
            <View style={styles.subItems}>
              {emails.map((email, emailIndex) => (
                <View key={emailIndex} style={styles.row}>
                  <TextInput
                    style={[
                      styles.input,
                      styles.typeInput,
                      { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
                    ]}
                    placeholder="Type"
                    placeholderTextColor={colors.placeholder}
                    value={email.type}
                    onChangeText={(value) => handleUpdateEmail(emailIndex, { type: value as EmailType })}
                  />
                  <TextInput
                    style={[
                      styles.input,
                      styles.flexInput,
                      { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
                    ]}
                    placeholder="email@example.com"
                    placeholderTextColor={colors.placeholder}
                    value={email.address}
                    onChangeText={(value) => handleUpdateEmail(emailIndex, { address: value })}
                    keyboardType="email-address"
                  />
                  <Button
                    icon="close"
                    style={[styles.removeBtn, { backgroundColor: colors.dangerBackground }]}
                    textStyle={{ color: colors.dangerText }}
                    onPress={() => handleRemoveEmail(emailIndex)}
                  />
                </View>
              ))}
            </View>
          )}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 12,
    overflow: "hidden",
  },
  itemContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
  },
  itemInfo: {
    flex: 1,
    gap: 4,
  },
  itemHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  contactDetails: {
    marginTop: 4,
    gap: 2,
  },
  detailText: {
    fontSize: 13,
  },
  editForm: {
    padding: 16,
    gap: 12,
  },
  row: {
    flexDirection: "row",
    gap: 8,
    alignItems: "center",
  },
  input: {
    height: 40,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
  },
  flexInput: {
    flex: 1,
  },
  typeInput: {
    width: 100,
  },
  label: {
    fontSize: 14,
    minWidth: 90,
  },
  subSection: {
    gap: 8,
    paddingTop: 8,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: "rgba(128, 128, 128, 0.2)",
  },
  subSectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  subItems: {
    gap: 8,
  },
  smallBtn: {
    height: 28,
    paddingHorizontal: 10,
    backgroundColor: "transparent",
    borderWidth: 1,
    gap: 4,
  },
  removeBtn: {
    height: 32,
    width: 32,
    paddingHorizontal: 0,
  },
  saveBtn: {
    height: 40,
    width: 40,
    paddingHorizontal: 0,
    borderRadius: 8,
  },
  cancelBtn: {
    height: 40,
    width: 40,
    paddingHorizontal: 0,
    borderRadius: 8,
    backgroundColor: "transparent",
    borderWidth: 1,
  },
});
